import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Inbox, CheckCircle2, Ban, X, BellRing, Mail } from "lucide-react";

const AUTO_SCAN_MIN = 30;
const d = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const GUESS = {
  granted: ["Lijkt JA", "bg-emerald-100 text-emerald-800"],
  denied: ["Lijkt NEE", "bg-red-100 text-red-700"],
  unknown: ["Onduidelijk", "bg-slate-100 text-slate-600"],
};

const ReplyRow = ({ s, onDecide }) => {
  const [label, cls] = GUESS[s.guess] || GUESS.unknown;
  return (
    <div data-testid={`reply-${s.id}`} className="flex flex-col gap-1.5 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-slate-900 truncate max-w-full">{s.name}</span>
        <span className={`text-[11px] rounded-full px-2 py-0.5 ${cls}`}>{label}</span>
        <span className="text-xs text-slate-400 ml-auto">{s.from} · {d(s.received_at)}</span>
      </div>
      <p className="text-xs text-slate-600 line-clamp-2 break-words">{s.snippet || <i>(geen tekst)</i>}</p>
      <div className="flex flex-wrap gap-1.5">
        <Button data-testid={`reply-grant-${s.id}`} size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => onDecide(s, "granted")}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Toestemming ✓
        </Button>
        <Button data-testid={`reply-deny-${s.id}`} size="sm" variant="outline" className="h-7 text-red-600 border-red-200 hover:bg-red-50" onClick={() => onDecide(s, "denied")}>
          <Ban className="w-3.5 h-3.5 mr-1" /> Geen toestemming
        </Button>
        <Button data-testid={`reply-dismiss-${s.id}`} size="sm" variant="ghost" className="h-7" onClick={() => onDecide(s, "dismiss")}>
          <X className="w-3.5 h-3.5 mr-1" /> Negeren
        </Button>
      </div>
    </div>
  );
};

const Overdue = ({ items, days }) => (
  <div data-testid="permission-overdue">
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
      <BellRing className="w-3.5 h-3.5" /> Geen reactie na {days} dagen ({items.length})
    </div>
    {items.length ? (
      <div className="max-h-56 overflow-auto rounded border border-slate-100 divide-y divide-slate-100 text-xs">
        {items.map((i) => (
          <div key={i.id} data-testid={`overdue-${i.id}`} className="flex items-center gap-2 px-2 py-1.5">
            <span className="flex-1 min-w-0">
              <span className="block truncate font-medium text-slate-800">{i.name}</span>
              <span className="block truncate text-slate-400">{[i.contact?.name || i.contact?.title, i.email, i.city].filter(Boolean).join(" · ")}</span>
            </span>
            <span className="shrink-0 text-amber-700 tabular-nums">{i.days_ago} dagen</span>
            <a data-testid={`overdue-mail-${i.id}`} href={`mailto:${i.email}`} title="Persoonlijk opvolgen"
              className="shrink-0 rounded p-1 text-slate-500 hover:bg-orange-50 hover:text-[#FF4500] transition-colors">
              <Mail className="w-3.5 h-3.5" />
            </a>
          </div>
        ))}
      </div>
    ) : <p className="text-xs text-slate-400">Niemand om op te volgen.</p>}
  </div>
);

export const PermissionFollowUp = ({ API, profile, onChanged }) => {
  const [replies, setReplies] = useState(null);
  const [overdue, setOverdue] = useState({ items: [], days: 10 });
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    const [r, o] = await Promise.all([axios.get(`${API}/mail/replies`), axios.get(`${API}/mail/permission-overdue`, { params: { profile } })]);
    setReplies(r.data); setOverdue(o.data);
    return r.data;
  }, [API, profile]);

  const scan = useCallback(async (quiet) => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/mail/replies/scan`);
      if (!quiet) {
        if (!data.mailboxes) toast.info("Nog geen afzender met wachtwoord — vul die eerst in bij Hostnet.");
        else toast.success(`${data.checked} mail(s) bekeken in ${data.mailboxes} inbox(en): ${data.found} nieuwe reactie(s)`);
      }
      data.errors?.forEach((e) => toast.error(`Inbox lezen mislukt — ${e}`));
      await load();
    } catch (e) { if (!quiet) toast.error(e?.response?.data?.detail || "Inbox controleren mislukt"); }
    setScanning(false);
  }, [API, load]);

  useEffect(() => {
    load().then((r) => {
      const age = r.last_scan ? (Date.now() - new Date(r.last_scan).getTime()) / 60000 : Infinity;
      if (age > AUTO_SCAN_MIN) scan(true);
    }).catch(() => {});
  }, [load, scan]);

  const decide = async (s, action) => {
    try {
      await axios.post(`${API}/mail/replies/${s.id}`, { action });
      toast.success(action === "granted" ? `${s.name}: toestemming gegeven` : action === "denied" ? `${s.name}: geen toestemming` : "Genegeerd");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };

  if (!replies) return null;
  return (
    <div data-testid="permission-followup" className="rounded-xl border border-slate-200 p-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-slate-900 mr-auto">Reacties op de toestemmingsmail</h3>
        <span data-testid="reply-last-scan" className="text-xs text-slate-400">{replies.last_scan ? `laatst gecontroleerd ${d(replies.last_scan)}` : "nog niet gecontroleerd"}</span>
        <Button data-testid="reply-scan-btn" size="sm" variant="outline" disabled={scanning} onClick={() => scan(false)}>
          <Inbox className={`w-3.5 h-3.5 mr-1 ${scanning ? "animate-pulse" : ""}`} /> {scanning ? "Inbox lezen…" : "Inbox controleren"}
        </Button>
      </div>
      <div data-testid="reply-suggestions">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Nieuwe antwoorden ({replies.items.length})</div>
        {replies.items.length ? (
          <div className="max-h-80 overflow-auto rounded border border-slate-100 divide-y divide-slate-100">
            {replies.items.map((s) => <ReplyRow key={s.id} s={s} onDecide={decide} />)}
          </div>
        ) : <p className="text-xs text-slate-400">Geen nieuwe antwoorden van gevraagde leads. De app kijkt alleen mee in je Hostnet-inbox en verandert daar niets.</p>}
      </div>
      <Overdue items={overdue.items} days={overdue.days} />
    </div>
  );
};
